import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Avatar, Badge, Button, Card, Col, Drawer, Empty, Form, Input, Modal,
  Row, Select, Space, Spin, Tabs, Tag, Typography, message,
} from "antd";
import {
  MessageOutlined, PlusOutlined, SendOutlined, SolutionOutlined,
  TeamOutlined, UserOutlined,
} from "@ant-design/icons";
import {
  createConsultation, getChatMessages, getConsultation, getConsultationReplies,
  getConsultations, getNotifications, getPatientHistoryByPin, getPeerChats,
  markNotificationsRead, replyToConsultation, searchDoctorPeers, searchPatients,
  sendChatMessage, startPeerChat, updateConsultation,
} from "../../services/doctorService";
import PatientHistoryView from "./PatientHistoryView";
import { handleApiError } from "../../utils/apiErrors";

const { Text, Title, Paragraph } = Typography;
const { Option } = Select;
const toArray = (d) => Array.isArray(d) ? d : (d?.results ?? []);
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString() : "";

const STATUS_COLOR   = { OPEN: "blue", IN_PROGRESS: "orange", RESOLVED: "green", CLOSED: "default" };
const PRIORITY_COLOR = { LOW: "default", NORMAL: "blue", HIGH: "orange", URGENT: "red" };

const formatDoctorLabel = (d) => {
  const name = [d.first_name, d.last_name].filter(Boolean).join(" ").trim();
  const spec = d.specialty || "General";
  return `${name || d.email} — ${spec}`;
};

const formatPatientLabel = (p) => {
  const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return `${name || p.email} · PIN ${p.pin ?? "—"}`;
};

export default function PeerConsultation({ doctorProfile }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") === "case" ? "cases" : "chat"
  );
  const deepLinkChatId = searchParams.get("chatId");
  const deepLinkCaseId = searchParams.get("caseId");
  const deepLinkHandled = useRef(false);

  // ── Chat state ──
  const [chats,          setChats]          = useState([]);
  const [activeChatId,   setActiveChatId]   = useState(null);
  const [messages,       setMessages]       = useState([]);
  const [chatLoading,    setChatLoading]    = useState(false);
  const [msgLoading,     setMsgLoading]     = useState(false);
  const [draft,          setDraft]          = useState("");
  const [newChatModal,   setNewChatModal]   = useState(false);
  const [peerOptions,    setPeerOptions]    = useState([]);
  const [peersLoading,   setPeersLoading]   = useState(false);
  const [newChatDoctor,  setNewChatDoctor]  = useState(null);
  const [patientOptions,  setPatientOptions]  = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);

  const peerSearchTimer    = useRef();
  const patientSearchTimer = useRef();

  // ── Case state ──
  const [cases,        setCases]        = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [newCaseModal, setNewCaseModal] = useState(false);
  const [caseForm]     = Form.useForm();
  const [caseDrawer,   setCaseDrawer]   = useState(null);  // active case
  const [caseReplies,  setCaseReplies]  = useState([]);
  const [caseLoading,  setCaseLoading]  = useState(false);
  const [replyDraft,   setReplyDraft]   = useState("");
  const [historyDrawer, setHistoryDrawer] = useState(null); // history data for case patient

  const messagesEndRef = useRef(null);

  const loadChats = useCallback(async () => {
    setChatLoading(true);
    try {
      const res = await getPeerChats();
      setChats(toArray(res.data));
    } catch { /* silent */ } finally { setChatLoading(false); }
  }, []);

  const loadCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const res = await getConsultations();
      setCases(toArray(res.data));
    } catch { /* silent */ } finally { setCasesLoading(false); }
  }, []);

  const runPeerSearch = useCallback(async (term = "") => {
    setPeersLoading(true);
    try {
      const res = await searchDoctorPeers(term);
      setPeerOptions(toArray(res.data));
    } catch { /* silent */ } finally { setPeersLoading(false); }
  }, []);

  const handlePeerSearch = (term) => {
    clearTimeout(peerSearchTimer.current);
    peerSearchTimer.current = setTimeout(() => runPeerSearch(term), 300);
  };

  const runPatientSearch = useCallback(async (term = "") => {
    setPatientsLoading(true);
    try {
      const res = await searchPatients(term);
      setPatientOptions(toArray(res.data));
    } catch { /* silent */ } finally { setPatientsLoading(false); }
  }, []);

  const handlePatientSearch = (term) => {
    clearTimeout(patientSearchTimer.current);
    patientSearchTimer.current = setTimeout(() => runPatientSearch(term), 300);
  };

  const loadChatMessages = useCallback(async (chatId) => {
    if (!chatId) return;
    setMsgLoading(true);
    try {
      const res = await getChatMessages(chatId);
      setMessages(toArray(res.data));
    } catch { /* silent */ } finally { setMsgLoading(false); }
  }, []);

  // Initial load
  useEffect(() => {
    loadChats();
    loadCases();
  }, [loadChats, loadCases]);

  const markRelatedNotificationsRead = useCallback(async (predicate) => {
    try {
      const res = await getNotifications();
      const rows = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
      const ids = rows.filter(predicate).map(n => n.id);
      if (ids.length > 0) await markNotificationsRead(ids);
    } catch { /* silent */ }
  }, []);

  const clearDeepLinkParams = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    ["tab", "chatId", "caseId"].forEach(k => next.delete(k));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => () => {
    clearTimeout(peerSearchTimer.current);
    clearTimeout(patientSearchTimer.current);
  }, []);

  // Poll for new messages in active chat every 5 seconds
  useEffect(() => {
    if (!activeChatId) return;
    loadChatMessages(activeChatId);
    const id = setInterval(() => loadChatMessages(activeChatId), 5000);
    return () => clearInterval(id);
  }, [activeChatId, loadChatMessages]);

  // Poll the chat list every 10 seconds for unread badges
  useEffect(() => {
    const id = setInterval(loadChats, 10000);
    return () => clearInterval(id);
  }, [loadChats]);

  // Auto-scroll chat to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages]);

  const handleStartChat = async () => {
    if (!newChatDoctor) return;
    try {
      const res = await startPeerChat(newChatDoctor);
      setNewChatModal(false);
      setNewChatDoctor(null);
      await loadChats();
      setActiveChatId(res.data?.id);
    } catch (err) {
      handleApiError(err, "Failed to start chat.");
    }
  };

  const handleSendMessage = async () => {
    const content = draft.trim();
    if (!content || !activeChatId) return;
    try {
      await sendChatMessage(activeChatId, content);
      setDraft("");
      loadChatMessages(activeChatId);
      loadChats();
    } catch (err) {
      handleApiError(err, "Failed to send message.");
    }
  };

  const handleCreateCase = async (values) => {
    try {
      const payload = {
        recipient:       values.recipient,
        subject:         values.subject,
        priority:        values.priority,
        patient:         values.patient || null,
        initial_message: values.initial_message,
      };
      await createConsultation(payload);
      message.success("Consultation case opened.");
      setNewCaseModal(false);
      caseForm.resetFields();
      loadCases();
    } catch (err) {
      handleApiError(err, "Failed to open consultation.");
    }
  };

  const openCase = useCallback(async (c) => {
    setCaseDrawer(c);
    setCaseLoading(true);
    setCaseReplies([]);
    try {
      const [caseRes, repliesRes] = await Promise.all([
        getConsultation(c.id),
        getConsultationReplies(c.id),
      ]);
      setCaseDrawer(caseRes.data);
      setCaseReplies(toArray(repliesRes.data));
      markRelatedNotificationsRead(
        (n) => !n.is_read
          && ["NEW_CASE_REQUEST", "NEW_CASE_REPLY"].includes(n.notification_type)
          && (n.link_url || "").includes(`caseId=${c.id}`)
      );
    } catch { /* silent */ } finally { setCaseLoading(false); }
  }, [markRelatedNotificationsRead]);

  // Deep-link: ?tab=chat&chatId= or ?tab=case&caseId=
  useEffect(() => {
    if (deepLinkHandled.current) return;
    if (deepLinkChatId && chats.length > 0) {
      const match = chats.find(c => c.id === deepLinkChatId);
      if (match) {
        setActiveTab("chat");
        setActiveChatId(match.id);
        deepLinkHandled.current = true;
        clearDeepLinkParams();
      }
    } else if (deepLinkCaseId && cases.length > 0) {
      const match = cases.find(c => c.id === deepLinkCaseId);
      if (match) {
        setActiveTab("cases");
        openCase(match);
        deepLinkHandled.current = true;
        clearDeepLinkParams();
      }
    }
  }, [chats, cases, deepLinkChatId, deepLinkCaseId, openCase, clearDeepLinkParams]);

  // When a chat becomes active, mark its related notifications as read.
  useEffect(() => {
    if (!activeChatId) return;
    markRelatedNotificationsRead(
      (n) => !n.is_read
        && n.notification_type === "NEW_CHAT_MESSAGE"
        && (n.link_url || "").includes(`chatId=${activeChatId}`)
    );
  }, [activeChatId, markRelatedNotificationsRead]);

  const handleReply = async () => {
    const content = replyDraft.trim();
    if (!content || !caseDrawer) return;
    try {
      await replyToConsultation(caseDrawer.id, content);
      setReplyDraft("");
      const res = await getConsultationReplies(caseDrawer.id);
      setCaseReplies(toArray(res.data));
      loadCases();
    } catch {
      message.error("Failed to post reply.");
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!caseDrawer) return;
    try {
      await updateConsultation(caseDrawer.id, { status: newStatus });
      const res = await getConsultation(caseDrawer.id);
      setCaseDrawer(res.data);
      loadCases();
    } catch {
      message.error("Failed to update status.");
    }
  };

  const handleViewCasePatient = async () => {
    if (!caseDrawer?.patient_pin) return;
    try {
      const res = await getPatientHistoryByPin(caseDrawer.patient_pin);
      setHistoryDrawer(res.data);
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to load history.");
    }
  };

  // ── Derived ─────────────────────────────────────────────────────────────────
  const activeChat = useMemo(
    () => chats.find(c => c.id === activeChatId),
    [chats, activeChatId]
  );
  const filteredCases = useMemo(() => {
    if (filterStatus === "ALL") return cases;
    return cases.filter(c => c.status === filterStatus);
  }, [cases, filterStatus]);
  const totalUnread = useMemo(
    () => chats.reduce((sum, c) => sum + (c.unread_count || 0), 0),
    [chats]
  );
  const otherEmail = (c) => {
    const emails = (c?.participant_emails ?? []).filter(e => e !== doctorProfile?.user_email);
    return emails[0] ?? "Doctor";
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>
        Peer Consultation
        {totalUnread > 0 && (
          <Badge count={totalUnread} style={{ marginLeft: 12, backgroundColor: "#ff4d4f" }} />
        )}
      </Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "chat",
            label: <span><MessageOutlined /> Quick Chat</span>,
            children: (
              <Card style={{ borderRadius: 8 }} styles={{ body: { padding: 0 } }}>
                <Row style={{ minHeight: 540 }}>
                  {/* Left — chat list */}
                  <Col xs={24} sm={8} md={7} style={{ borderRight: "1px solid #f0f0f0" }}>
                    <div style={{ padding: 12, borderBottom: "1px solid #f0f0f0" }}>
                      <Button type="primary" block icon={<PlusOutlined />}
                        onClick={() => setNewChatModal(true)}>
                        Start New Chat
                      </Button>
                    </div>
                    <Spin spinning={chatLoading}>
                      <div style={{ maxHeight: 480, overflowY: "auto" }}>
                        {chats.length === 0 ? (
                          <Empty
                            description="No conversations yet. Click 'Start New Chat' to message a colleague."
                            style={{ padding: 24 }}
                          />
                        ) : chats.map(c => (
                          <div key={c.id}
                            onClick={() => setActiveChatId(c.id)}
                            style={{
                              padding: "12px 14px",
                              cursor: "pointer",
                              borderBottom: "1px solid #f5f5f5",
                              background: activeChatId === c.id ? "#e6f4ff" : "transparent",
                            }}>
                            <Row align="middle" gutter={10}>
                              <Col>
                                <Badge count={c.unread_count || 0} offset={[-4, 4]}>
                                  <Avatar icon={<UserOutlined />} />
                                </Badge>
                              </Col>
                              <Col flex="auto" style={{ minWidth: 0 }}>
                                <Text strong style={{ display: "block" }} ellipsis>
                                  {otherEmail(c)}
                                </Text>
                                <Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                                  {c.last_message?.content?.slice(0, 40) || "—"}
                                </Text>
                              </Col>
                            </Row>
                          </div>
                        ))}
                      </div>
                    </Spin>
                  </Col>

                  {/* Right — message pane */}
                  <Col xs={24} sm={16} md={17} style={{ display: "flex", flexDirection: "column" }}>
                    {activeChat ? (
                      <>
                        <div style={{
                          padding: "12px 16px", borderBottom: "1px solid #f0f0f0",
                          background: "#fafafa",
                        }}>
                          <Text strong><TeamOutlined /> {otherEmail(activeChat)}</Text>
                        </div>
                        <div ref={messagesEndRef} style={{
                          flex: 1, minHeight: 360, maxHeight: 420, padding: 16,
                          overflowY: "auto", background: "#f5f5f5",
                        }}>
                          <Spin spinning={msgLoading}>
                            {messages.length === 0 ? (
                              <Empty description="No messages yet" style={{ marginTop: 60 }} />
                            ) : messages.map(m => {
                              const isMine = m.sender === doctorProfile?.id;
                              return (
                                <div key={m.id} style={{
                                  display: "flex",
                                  justifyContent: isMine ? "flex-end" : "flex-start",
                                  marginBottom: 10,
                                }}>
                                  <div style={{
                                    maxWidth: "72%",
                                    padding: "8px 12px",
                                    borderRadius: 12,
                                    background: isMine ? "#1677ff" : "#fff",
                                    color: isMine ? "#fff" : "#262626",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                  }}>
                                    <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{m.content}</div>
                                    <div style={{
                                      fontSize: 10, marginTop: 4, opacity: 0.75,
                                      textAlign: "right",
                                    }}>{fmtTime(m.created_at)}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </Spin>
                        </div>
                        <div style={{ padding: 12, borderTop: "1px solid #f0f0f0" }}>
                          <Row gutter={8}>
                            <Col flex="auto">
                              <Input.TextArea
                                autoSize={{ minRows: 1, maxRows: 3 }}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                                placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
                              />
                            </Col>
                            <Col>
                              <Button type="primary" icon={<SendOutlined />} onClick={handleSendMessage}>
                                Send
                              </Button>
                            </Col>
                          </Row>
                        </div>
                      </>
                    ) : (
                      <Empty description="Select a chat or start a new one" style={{ margin: "auto" }} />
                    )}
                  </Col>
                </Row>
              </Card>
            ),
          },
          {
            key: "cases",
            label: <span><SolutionOutlined /> Case Consultations</span>,
            children: (
              <Card
                style={{ borderRadius: 8 }}
                title={
                  <Space>
                    <Select
                      value={filterStatus}
                      onChange={setFilterStatus}
                      style={{ width: 160 }}
                      options={[
                        { value: "ALL",         label: "All" },
                        { value: "OPEN",        label: "Open" },
                        { value: "IN_PROGRESS", label: "In Progress" },
                        { value: "RESOLVED",    label: "Resolved" },
                        { value: "CLOSED",      label: "Closed" },
                      ]}
                    />
                  </Space>
                }
                extra={
                  <Button type="primary" icon={<PlusOutlined />}
                    onClick={() => setNewCaseModal(true)}>
                    Start New Consultation
                  </Button>
                }>
                <Spin spinning={casesLoading}>
                  {filteredCases.length === 0 ? (
                    <Empty
                      description={
                        filterStatus === "ALL"
                          ? "No consultation cases yet. Click 'Start New Consultation' to request a peer review on a patient case."
                          : `No ${filterStatus.toLowerCase().replace("_", " ")} cases. Try changing the status filter.`
                      }
                    />
                  ) : (
                    <div>
                      {filteredCases.map(c => {
                        const isInitiator = c.initiator === doctorProfile?.id;
                        const counterpart = isInitiator ? c.recipient_email : c.initiator_email;
                        return (
                          <Card key={c.id} size="small" style={{ marginBottom: 10, borderRadius: 6 }}
                            hoverable onClick={() => openCase(c)}>
                            <Row align="middle" gutter={12}>
                              <Col flex="auto">
                                <Text strong style={{ fontSize: 15 }}>{c.subject}</Text>
                                <div style={{ marginTop: 4 }}>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {isInitiator ? "To" : "From"}: {counterpart ?? "—"}
                                    {c.patient_pin && <> &nbsp;·&nbsp; Patient PIN: <Text code>{c.patient_pin}</Text></>}
                                  </Text>
                                </div>
                              </Col>
                              <Col>
                                <Space>
                                  <Tag color={PRIORITY_COLOR[c.priority] ?? "default"}>{c.priority}</Tag>
                                  <Tag color={STATUS_COLOR[c.status] ?? "default"}>{c.status?.replace(/_/g, " ")}</Tag>
                                  <Text type="secondary" style={{ fontSize: 12 }}>{fmtTime(c.updated_at)}</Text>
                                </Space>
                              </Col>
                            </Row>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </Spin>
              </Card>
            ),
          },
        ]}
      />

      {/* ── New Chat Modal ── */}
      <Modal
        title="Start New Chat"
        open={newChatModal}
        onOk={handleStartChat}
        okText="Start Chat"
        okButtonProps={{ disabled: !newChatDoctor }}
        onCancel={() => { setNewChatModal(false); setNewChatDoctor(null); }}
        afterOpenChange={(o) => o && peerOptions.length === 0 && runPeerSearch("")}
        destroyOnHidden>
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Select a doctor from the MCHUSA network to start a private chat.
        </Text>
        <Select
          showSearch
          filterOption={false}
          placeholder="Search doctor by name, email, or specialty…"
          style={{ width: "100%" }}
          loading={peersLoading}
          notFoundContent={
            peersLoading
              ? <Spin size="small" />
              : "No doctors found matching your search. Only active doctors on the MCHUSA network appear here."
          }
          onSearch={handlePeerSearch}
          options={peerOptions.map(d => ({
            value: d.doctor_profile_id,
            label: formatDoctorLabel(d),
          }))}
          value={newChatDoctor}
          onChange={setNewChatDoctor}
        />
      </Modal>

      {/* ── New Case Modal ── */}
      <Modal
        title="Start New Consultation"
        open={newCaseModal}
        onOk={() => caseForm.submit()}
        okText="Open Consultation"
        onCancel={() => { setNewCaseModal(false); caseForm.resetFields(); }}
        afterOpenChange={(o) => {
          if (!o) return;
          if (peerOptions.length === 0) runPeerSearch("");
          if (patientOptions.length === 0) runPatientSearch("");
        }}
        destroyOnHidden
        width={560}>
        <Form
          form={caseForm}
          layout="vertical"
          onFinish={handleCreateCase}
          initialValues={{ priority: "NORMAL" }}>
          <Form.Item name="recipient" label="Recipient Doctor"
            rules={[{ required: true, message: "Select a recipient" }]}>
            <Select
              showSearch
              filterOption={false}
              placeholder="Search doctor by name, email, or specialty…"
              loading={peersLoading}
              notFoundContent={
                peersLoading
                  ? <Spin size="small" />
                  : "No doctors found matching your search. Only active doctors on the MCHUSA network appear here."
              }
              onSearch={handlePeerSearch}
              options={peerOptions.map(d => ({
                value: d.doctor_profile_id,
                label: formatDoctorLabel(d),
              }))}
            />
          </Form.Item>
          <Form.Item name="subject" label="Subject"
            rules={[{ required: true, message: "Enter a subject" }]}>
            <Input placeholder="Brief title for this consultation" maxLength={255} />
          </Form.Item>
          <Form.Item name="priority" label="Priority">
            <Select>
              <Option value="LOW">Low</Option>
              <Option value="NORMAL">Normal</Option>
              <Option value="HIGH">High</Option>
              <Option value="URGENT">Urgent</Option>
            </Select>
          </Form.Item>
          <Form.Item name="patient" label="Attach Patient (optional)"
            tooltip="Optionally attach a patient — the recipient can then view their full history.">
            <Select
              showSearch
              allowClear
              filterOption={false}
              placeholder="Search patients by name, email, or PIN…"
              loading={patientsLoading}
              notFoundContent={
                patientsLoading
                  ? <Spin size="small" />
                  : "No patients found matching your search. Try searching by name, email, or PIN."
              }
              onSearch={handlePatientSearch}
              options={patientOptions.map(p => ({
                value: p.patient_profile_id,
                label: formatPatientLabel(p),
              }))}
            />
          </Form.Item>
          <Form.Item name="initial_message" label="Initial Message"
            rules={[{ required: true, message: "Add an initial message" }]}>
            <Input.TextArea rows={5} placeholder="Describe the case and what input you need…" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Case Detail Drawer ── */}
      <Drawer
        open={!!caseDrawer}
        onClose={() => { setCaseDrawer(null); setCaseReplies([]); }}
        size="large"
        title={caseDrawer?.subject ?? "Consultation"}
        destroyOnHidden>
        <Spin spinning={caseLoading}>
          {caseDrawer && (
            <>
              <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
                <Col><Tag color={PRIORITY_COLOR[caseDrawer.priority] ?? "default"}>{caseDrawer.priority}</Tag></Col>
                <Col>
                  <Select
                    value={caseDrawer.status}
                    onChange={handleStatusChange}
                    style={{ minWidth: 160 }}
                    options={[
                      { value: "OPEN",        label: "Open" },
                      { value: "IN_PROGRESS", label: "In Progress" },
                      { value: "RESOLVED",    label: "Resolved" },
                      { value: "CLOSED",      label: "Closed" },
                    ]}
                  />
                </Col>
              </Row>
              <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                <b>From:</b> {caseDrawer.initiator_email ?? "—"} &nbsp;·&nbsp;
                <b>To:</b> {caseDrawer.recipient_email ?? "—"}
              </Paragraph>
              {caseDrawer.patient_pin && (
                <Card size="small" style={{ marginBottom: 16, background: "#f5faff", borderRadius: 6 }}>
                  <Row align="middle" justify="space-between">
                    <Col>
                      <Text strong>Attached Patient</Text><br />
                      <Text type="secondary">
                        {caseDrawer.patient_email ?? "—"} · PIN <Text code>{caseDrawer.patient_pin}</Text>
                      </Text>
                    </Col>
                    <Col>
                      <Button onClick={handleViewCasePatient}>View Full Patient History</Button>
                    </Col>
                  </Row>
                </Card>
              )}

              <Title level={5} style={{ marginTop: 8 }}>Thread</Title>
              <div style={{ maxHeight: 360, overflowY: "auto", marginBottom: 12 }}>
                {caseReplies.length === 0 ? (
                  <Empty description="No replies yet" />
                ) : caseReplies.map(r => {
                  const isMine = r.author === doctorProfile?.id;
                  return (
                    <Card key={r.id} size="small"
                      style={{
                        marginBottom: 8,
                        borderRadius: 6,
                        borderLeft: `3px solid ${isMine ? "#1677ff" : "#d9d9d9"}`,
                      }}>
                      <Row justify="space-between" style={{ marginBottom: 4 }}>
                        <Text strong style={{ fontSize: 12 }}>
                          {isMine ? "You" : r.author_email}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>{fmtTime(r.created_at)}</Text>
                      </Row>
                      <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{r.content}</div>
                    </Card>
                  );
                })}
              </div>

              <Row gutter={8}>
                <Col flex="auto">
                  <Input.TextArea
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                    placeholder="Add a reply…"
                  />
                </Col>
                <Col>
                  <Button type="primary" icon={<SendOutlined />} onClick={handleReply}>
                    Reply
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </Spin>
      </Drawer>

      {/* ── Patient History (from inside a case) ── */}
      <Drawer
        open={!!historyDrawer}
        onClose={() => setHistoryDrawer(null)}
        size="large"
        title="Patient Medical History"
        destroyOnHidden>
        {historyDrawer && <PatientHistoryView data={historyDrawer} />}
      </Drawer>
    </>
  );
}
